import { DateTime } from 'luxon';
import { isEventBenign } from './error-catalog';
import {
  INTEGRATION_STATUS,
  type IntegrationStatus,
  type MonitorEvent,
  type TaggedMonitorEvent,
} from './types';

// Only failures this recent can flag an integration — older failures on
// long-dead flows must not red-flag a currently healthy sync. The monitor query
// filters on the same window, so this is the single definition of it.
export const ATTENTION_WINDOW_DAYS = 7;
// A failure with this many retry attempts is considered exhausted even if
// another retry is scheduled.
const RETRY_EXHAUST_THRESHOLD = 3;

// Mirrors event-accordion.tsx. skipped_at is ignored entirely, which is what
// makes skipped-but-failed count as failed (known backend bug marks some
// failed events as skipped) — there is no explicit skipped branch.
function isEventFailed(evt: MonitorEvent): boolean {
  return !!evt.failed_at && !evt.succeeded_at;
}

type DerivedAgentStatus = {
  status: IntegrationStatus;
  lastSyncAt: string | null;
  hasFailures: boolean;
  failingEvents: MonitorEvent[];
  monitorUnavailable: boolean;
};

export function deriveAgentStatus(
  input: {
    isActive: boolean | null | undefined;
    events: TaggedMonitorEvent[];
    lastSyncAt?: string | null;
    monitorErrored: boolean;
    applicationId: string;
    serviceId: string;
  },
  now: DateTime = DateTime.now()
): DerivedAgentStatus {
  const {
    isActive,
    events,
    monitorErrored,
    applicationId,
    serviceId,
    lastSyncAt = null,
  } = input;

  if (isActive === false) {
    return {
      status: INTEGRATION_STATUS.INACTIVE,
      lastSyncAt: null,
      hasFailures: events.length > 0,
      failingEvents: [],
      monitorUnavailable: false,
    };
  }

  const windowStart = now.minus({ days: ATTENTION_WINDOW_DAYS });
  const failingEvents = events
    .filter(
      (evt) =>
        isEventFailed(evt) && DateTime.fromISO(evt.created_at) >= windowStart
    )
    // Never trust server order (event-list-card.tsx re-sorts for the same
    // reason).
    .sort((aEvent, bEvent) =>
      bEvent.created_at.localeCompare(aEvent.created_at)
    );

  const hasPendingRetry = (evt: MonitorEvent) =>
    !!evt.next_retry &&
    DateTime.fromISO(evt.next_retry) > now &&
    evt.retry_scheduled_amount < RETRY_EXHAUST_THRESHOLD;

  const actionableEvents = failingEvents.filter(
    (evt) =>
      !hasPendingRetry(evt) && !isEventBenign(applicationId, serviceId, evt)
  );

  if (actionableEvents.length > 0) {
    return {
      status: INTEGRATION_STATUS.ACTION_REQUIRED,
      lastSyncAt,
      hasFailures: true,
      failingEvents: actionableEvents,
      monitorUnavailable: false,
    };
  }

  return {
    status: INTEGRATION_STATUS.ACTIVE,
    lastSyncAt,
    hasFailures: events.length > 0,
    failingEvents: [],
    monitorUnavailable: monitorErrored,
  };
}
